import React, {FC} from 'react';
import styled from 'styled-components';

import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {Skeleton} from '../../../../../components/common/skeleton';
import {ProgressBarSerie, ProgressSerie} from './progressBarSerie';

/*
 * Props.
 */

export interface ProgressBarProps {
  series: ReadonlyArray<ProgressSerie>;
  height?: number;
  isLoading?: boolean;
  borderRadius?: number;
  total?: number;
  hideEmptyValues?: boolean;
  /** Callback when a serie is clicked */
  onClick?: (index: number, serie: ProgressSerie) => void;
  /** To set the hover state on a serie from outside the component */
  selectedSerieIndex?: number;
}

/*
 * Styles.
 */

const StyledProgressBarDiv = styled.div<{$height: number; $color: string; $radius: number}>`
  display: flex;
  align-items: stretch;
  overflow: hidden;

  height: ${(p) => `${p.$height}px`};
  border-radius: ${(p) => `${p.$radius}px`};

  background-color: ${(p) => p.$color};
`;

const StyledSkeletonWrapperDiv = styled.div`
  display: flex;
`;

const StyledSkeleton = styled(Skeleton)<{$radius: number}>`
  border-radius: ${(p) => `${p.$radius}px`};
`;

const StyledLegendWrapperDiv = styled.div`
  ${flexCenter};
  margin-top: 8px;
`;

const StyledSerieLegendDiv = styled.div<{$width: number}>`
  ${flexCenter};
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.medium};
  line-height: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade90};
  width: ${(p) => (p.$width > 0 ? `${p.$width}%` : 'auto')};
  white-space: nowrap;
  &:last-child {
    justify-content: end;
  }
`;

const StyledSerieLegendDotDiv = styled.div<{$color: string}>`
  background-color: ${(p) => p.$color};
  width: 8px;
  height: 8px;
  margin-right: 8px;
  border-radius: 50%;
`;

/*
 * Component.
 */

export const ProgressBar: FC<ProgressBarProps> = ({
  isLoading,
  series,
  height = 12,
  borderRadius = 50,
  total = 100,
  hideEmptyValues = true,
  onClick,
  selectedSerieIndex,
}) => {
  const theme = useUserTheme();
  if (isLoading) {
    return (
      // Wrapping with flex div to avoid weird margin issue with narrow progress bars https://github.com/frontapp/components/pull/388.
      <StyledSkeletonWrapperDiv>
        <StyledSkeleton width="100%" height={`${height}px`} $radius={borderRadius} />
      </StyledSkeletonWrapperDiv>
    );
  }

  return (
    <>
      <StyledProgressBarDiv $height={height} $color={theme.palette.grey.shade20} $radius={borderRadius}>
        {series.map((serie, i) => (
          <ProgressBarSerie
            key={serie.color}
            index={i}
            serie={serie}
            radius={borderRadius}
            total={total}
            hideEmptyValues={hideEmptyValues}
            onClick={onClick}
            selectedSerieIndex={selectedSerieIndex}
          />
        ))}
      </StyledProgressBarDiv>
      {maybeDisplayLegend(series, total)}
    </>
  );
};

/*
 * Helpers.
 */

function maybeDisplayLegend(series: ReadonlyArray<ProgressSerie>, total: number) {
  if (series.every((serie) => !serie.legendRenderer)) {
    return null;
  }

  return (
    <StyledLegendWrapperDiv>
      {series.map((serie) => (
        <StyledSerieLegendDiv key={serie.color} $width={(serie.value * 100) / total}>
          {serie.legendRenderer ? (
            <>
              <StyledSerieLegendDotDiv $color={serie.color} />
              {serie.legendRenderer(serie.value)}
            </>
          ) : null}
        </StyledSerieLegendDiv>
      ))}
    </StyledLegendWrapperDiv>
  );
}
